import { AssetType, PropertyPurpose } from '@/helpers/const';
import { useFeatureFlags, FeatureFlag } from '@/helpers/useFeatureFlags';
import { yupAddress, yupBoolean, yupNumber, yupObject, yupPeriodicValue, yupString } from '../../yup';

const isNegativeGearingFy27Enabled = () => useFeatureFlags().isEnabled(FeatureFlag.NEGATIVE_GEARING_FY27);

const isNewBuildAssetApplicable = (type?: string, propertyPurpose?: string) =>
  isNegativeGearingFy27Enabled() &&
  type === AssetType.PROPERTY &&
  propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY;

/**
 * Checks if the asset type can have residual debt questions.
 * Property and gifted funds are excluded.
 */
const canHaveResidualDebt = (type: string): boolean => type !== AssetType.PROPERTY && type !== AssetType.GIFTED_FUND;

const isAccountAsset = (val: string) =>
  val === AssetType.SAVINGS ||
  val === AssetType.SHARES ||
  val === AssetType.SUPERANNUATION ||
  val === AssetType.GIFTED_FUND;

export const assetSchema = yupObject({
  type: yupString().required().label('Asset type'),
  address: yupAddress()
    .when('type', { is: AssetType.PROPERTY, then: (schema) => schema.required() })
    .label('Address'),
  propertyPurpose: yupString()
    .when('type', { is: AssetType.PROPERTY, then: (schema) => schema.required() })
    .label('Property purpose'),
  propertyValue: yupNumber()
    .when('type', { is: AssetType.PROPERTY, then: (schema) => schema.required() })
    .label('Property value'),
  rentalIncome: yupPeriodicValue(yupNumber().required())
    .when('type', { is: AssetType.PROPERTY, then: (schema) => schema.required() })
    .label('Rental income'),
  amountOwing: yupNumber()
    .when('type', { is: AssetType.PROPERTY, then: (schema) => schema.required() })
    .label('Amount owing'),
  isNewBuild: yupBoolean()
    .when(['type', 'propertyPurpose'], ([type, propertyPurpose], schema) =>
      isNewBuildAssetApplicable(type, propertyPurpose) ? schema.required() : schema,
    )
    .label('Is new build'),
  isContractExchanged: yupBoolean()
    .when(['type', 'propertyPurpose', 'isNewBuild'], ([type, propertyPurpose, isNewBuild], schema) =>
      isNewBuildAssetApplicable(type, propertyPurpose) && isNewBuild === false ? schema.required() : schema,
    )
    .label('Contract exchanged on or before 12 May 2026'),
  institution: yupString()
    .when('type', { is: isAccountAsset, then: (schema) => schema.required() })
    .label('Institution'),
  value: yupNumber()
    .when('type', { is: (val: string) => val !== AssetType.PROPERTY, then: (schema) => schema.required() })
    .label('Asset value'),
  make: yupString()
    .when('type', { is: AssetType.MOTOR_VEHICLES, then: (schema) => schema.required() })
    .label('Vehicle make'),
  model: yupString()
    .when('type', { is: AssetType.MOTOR_VEHICLES, then: (schema) => schema.required() })
    .label('Vehicle model'),
  yearBought: yupString()
    .when('type', { is: AssetType.MOTOR_VEHICLES, then: (schema) => schema.required() })
    .matches(/^[0-9]*$/, 'Numbers only')
    .length(4)
    .test(
      'not-in-future',
      'Year bought cannot be in the future',
      (value) => !value || Number(value) <= new Date().getFullYear(),
    )
    .label('Year bought'),
  description: yupString()
    .when('type', {
      is: (val: string) => val === AssetType.MACHINERY || val === AssetType.EQUIPMENT,
      then: (schema) => schema.required(),
    })
    .label('Description'),
  useAssetForResidualDebt: yupBoolean().label('Use asset for residual debt'),
  amountTowardsResidualDebt: yupNumber()
    .when(['useAssetForResidualDebt', 'type'], {
      is: (useForDebt: boolean, type: string) => useForDebt === true && canHaveResidualDebt(type),
      then: (schema) => schema.required().min(0),
    })
    .label('Amount towards residual debt'),
});
